import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Shield, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight,
  Library, ExternalLink, Filter, FileDown, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import ClauseIndexCardDialog from "@/components/ClauseIndexCardDialog";

const CATEGORY_COLORS = {
  Management: "#1a1a1a",
  HACCP: "#ff4500",
  Sanitation: "#1a5fb4",
  Allergen: "#a13ad6",
  "Food Defense": "#d6a900",
  Supplier: "#1a8754",
  Traceability: "#0891b2",
  Personnel: "#7c2d12",
  GMP: "#0284c7",
  "Foreign Material": "#c026d3",
  Micro: "#dc2626",
  Operations: "#4b5563",
  Maintenance: "#78716c",
};

const FRAMEWORK_COLORS = { sqf: "#1a5fb4", iso22000: "#a13ad6", fsma: "#1a8754" };
const FRAMEWORK_LABELS = { sqf: "SQF", iso22000: "ISO 22000", fsma: "FSMA" };

/**
 * ComplianceCoverage — consolidated view. Each clause row cites all applicable
 * frameworks. Show-covered toggle removed (per user feedback). Gap rows offer
 * one-click install of the correct V&V template into the org.
 *
 * `embedded=true` renders without hero/back button — used inside CMD Health.
 */
export default function ComplianceCoverage({ embedded = false }) {
  const [data, setData] = useState(null);
  const [framework, setFramework] = useState("all");
  const [expanded, setExpanded] = useState(new Set());
  const [installing, setInstalling] = useState(null);
  const [orgStandards, setOrgStandards] = useState([]);
  const [showUntagged, setShowUntagged] = useState(true);
  const [viewClauseId, setViewClauseId] = useState(null);
  const nav = useNavigate();

  const load = () => {
    api.get("/compliance/coverage")
      .then((r) => setData(r.data))
      .catch(() => setData({ frameworks: [], clauses: [], summary: {} }));
    api.get("/orgs/me").then((r) => setOrgStandards(r.data.org?.active_standards || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const visibleClauses = useMemo(() => {
    if (!data) return [];
    let list = data.clauses || [];
    // Org-wide standards filter (Controller-configured)
    if (orgStandards.length > 0) {
      list = list.filter((c) => {
        const cFw = (c.citations || []).map((x) => x.framework);
        if (cFw.length === 0) return showUntagged;
        return cFw.some((fw) => orgStandards.includes(fw));
      });
    }
    if (framework !== "all") {
      list = list.filter((c) => (c.citations || []).some((cit) => cit.framework === framework));
    }
    return list;
  }, [data, framework, orgStandards, showUntagged]);

  const installVV = async (key, clauseId) => {
    if (!key) { toast.error("No V&V template configured for this clause yet"); return; }
    setInstalling(clauseId);
    try {
      const r = await api.post("/template-library/vv/install", { keys: [key] });
      const installed = (r.data.installed || [])[0];
      toast.success(`Installed: ${installed?.title || key}`);
      if (installed?.id) {
        nav(`/app/vv/${installed.id}`);
      } else {
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Install failed");
    } finally {
      setInstalling(null);
    }
  };

  const installChecklist = async (key, clauseId) => {
    if (!key) { toast.error("No checklist template configured for this clause yet"); return; }
    setInstalling(clauseId);
    try {
      await api.post("/template-library/checklists/install", { keys: [key] });
      toast.success("Checklist template installed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Install failed");
    } finally {
      setInstalling(null);
    }
  };

  const downloadReport = async () => {
    try {
      const r = await api.get("/compliance/coverage/report.pdf", { responseType: "blob" });
      const sha = r.headers?.["x-content-sha256"] || "";
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compliance-coverage-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(sha ? `PDF downloaded · SHA-256: ${sha.slice(0, 12)}…` : "PDF downloaded");
    } catch (e) {
      toast.error("Report generation failed");
    }
  };

  if (!data) {
    return <div className="p-10 font-mono text-sm text-[#1a1a1a]/50">Loading compliance coverage…</div>;
  }

  const s = data.summary || { total: 0, covered: 0, gaps: 0, percent: 0 };

  return (
    <div className={embedded ? "space-y-4" : "p-4 lg:p-8 space-y-5"} data-testid="compliance-coverage-page">
      {!embedded && (
        <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden" data-testid="cc-hero">
          <div className="absolute inset-0 dotted opacity-25" />
          <div className="absolute -top-16 -right-8 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
                <Shield className="w-3.5 h-3.5" />
                <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Compliance Coverage · SQF · ISO 22000 · FSMA</span>
              </div>
              <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl leading-tight tracking-tight">Compliance Coverage.</h1>
              <p className="mt-2 text-sm text-white/75 max-w-xl">
                Consolidated view of every regulatory clause your org is expected to cover. Multi-framework citations are collapsed into a single row.
              </p>
            </div>
            <div className="flex items-center gap-6" data-testid="cc-summary">
              <PercentDial percent={s.percent} label="Overall" />
              <div className="text-white">
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/60">Covered</div>
                <div className="font-display font-black text-2xl">{s.covered}/{s.total}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/60 mt-2">Gaps</div>
                <div className="font-display font-black text-2xl text-[#ff4500]">{s.gaps}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/60 mt-2">
                  {s.vv_items || 0} V&amp;V · {s.checklist_templates || 0} audits
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {embedded && (
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Compliance</div>
            <div className="font-display font-bold text-base">Coverage — SQF · ISO 22000 · FSMA</div>
            <div className="text-[11px] text-[#1a1a1a]/60 mt-0.5">
              {s.covered}/{s.total} clauses covered · <strong className="text-[#ff4500]">{s.gaps} gaps</strong> · {s.percent}%
            </div>
          </div>
          <button onClick={downloadReport} data-testid="cc-report-btn"
            className="btn-pill btn-pill-primary text-xs">
            <FileDown className="w-3 h-3" /> Auditor report
          </button>
        </div>
      )}

      {/* Framework cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(data.frameworks || []).map((f) => (
          <button key={f.key}
            onClick={() => setFramework(framework === f.key ? "all" : f.key)}
            data-testid={`fw-card-${f.key}`}
            className={`surface-card rounded-2xl p-5 text-left hover:shadow-lift transition-all ${framework === f.key ? "ring-2 ring-[#ff4500]" : ""}`}>
            <div className="flex items-center gap-2 mb-1" style={{ color: f.color }}>
              <Shield className="w-4 h-4" />
              <span className="font-mono text-[10px] uppercase tracking-wider font-bold">{f.name}</span>
            </div>
            <div className="flex items-end gap-2 mb-2">
              <div className="font-display font-black text-3xl">{f.percent}%</div>
              <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60 mb-1">{f.covered}/{f.total} covered</div>
            </div>
            <div className="w-full h-2 rounded-full bg-[#1a1a1a]/10 overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${f.percent}%`, background: f.color }} />
            </div>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-[#1a1a1a]/50" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
            {framework === "all" ? "All frameworks" : FRAMEWORK_LABELS[framework]}
          </span>
          {framework !== "all" && (
            <button onClick={() => setFramework("all")} data-testid="cc-clear-fw"
              className="text-[10px] uppercase tracking-wider text-[#ff4500] font-bold">Clear</button>
          )}
          {orgStandards.length > 0 && (
            <>
              <span className="text-[10px] uppercase tracking-wider text-[#1a1a1a]/40 mx-1">·</span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/60">
                Org filter:
              </span>
              {orgStandards.map((s) => (
                <span key={s} className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#ff4500]/12 text-[#ff4500]">
                  {FRAMEWORK_LABELS[s] || s}
                </span>
              ))}
              <button data-testid="cc-toggle-untagged" onClick={() => setShowUntagged((v) => !v)}
                className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                  showUntagged ? "bg-[#1a1a1a]/8 text-[#1a1a1a]/70" : "bg-transparent border border-[#1a1a1a]/15 text-[#1a1a1a]/45 line-through"
                }`}>
                {showUntagged ? "Untagged ON" : "Untagged OFF"}
              </button>
            </>
          )}
        </div>
        {!embedded && (
          <button onClick={downloadReport} data-testid="cc-report-btn"
            className="btn-pill btn-pill-primary text-xs">
            <FileDown className="w-3 h-3" /> Download auditor report
          </button>
        )}
      </div>

      {/* Clause list */}
      <div className="space-y-2">
        {visibleClauses.length === 0 && (
          <div className="surface-card rounded-2xl py-16 text-center text-[12px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
            No clauses to show
          </div>
        )}
        {visibleClauses.map((c) => {
          const isExpanded = expanded.has(c.id);
          const bar = c.covered ? "#1a8754" : "#ff4500";
          return (
            <div key={c.id} data-testid={`cc-clause-${c.id}`}
              className="surface-card rounded-xl overflow-hidden">
              <div className="flex items-stretch">
                <div className="w-1.5" style={{ background: bar }} />
                <div className="flex-1 flex items-center gap-3 p-3">
                  <button onClick={() => toggleExpand(c.id)}
                    data-testid={`cc-clause-expand-${c.id}`}
                    className="flex-1 flex items-center gap-3 text-left hover:opacity-80 transition-opacity min-w-0">
                    {c.covered ? (
                      <CheckCircle2 className="w-5 h-5 text-[#1a8754] flex-shrink-0" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-[#ff4500] flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {(c.citations || []).map((cit, i) => (
                          <span key={i}
                            className="font-mono text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: `${FRAMEWORK_COLORS[cit.framework] || "#1a1a1a"}18`, color: FRAMEWORK_COLORS[cit.framework] || "#1a1a1a" }}>
                            {FRAMEWORK_LABELS[cit.framework]} {cit.ref}
                          </span>
                        ))}
                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full"
                          style={{ background: `${CATEGORY_COLORS[c.category] || "#1a1a1a"}18`, color: CATEGORY_COLORS[c.category] || "#1a1a1a" }}>
                          {c.category}
                        </span>
                        {c.covered && (
                          <span className="text-[9px] uppercase tracking-wider font-bold text-[#1a8754]">
                            {c.vv_hit_count} V&amp;V · {c.checklist_hit_count} audit{c.checklist_hit_count === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                      <div className="font-display font-bold text-sm text-[#1a1a1a] mt-1">{c.title}</div>
                    </div>
                  </button>
                  <button onClick={() => setViewClauseId(c.id)}
                    data-testid={`cc-clause-view-${c.id}`}
                    className="p-1.5 rounded-lg bg-[#1a5fb4]/10 text-[#1a5fb4] hover:bg-[#1a5fb4]/20 flex-shrink-0"
                    title="Open Index Card">
                    <BookOpen className="w-4 h-4" />
                  </button>
                  <button onClick={() => toggleExpand(c.id)} className="flex-shrink-0 p-1.5" data-testid={`cc-clause-chevron-${c.id}`}>
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-[#1a1a1a]/40" /> : <ChevronRight className="w-4 h-4 text-[#1a1a1a]/40" />}
                  </button>
                </div>
              </div>
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 ml-6 space-y-2 border-t border-[#1a1a1a]/06 bg-[#f7f5f2]">
                  {!c.covered ? (
                    <div className="pt-2">
                      <div className="text-[11px] font-mono uppercase tracking-wider text-[#ff4500] font-bold mb-1">
                        Gap — nothing scheduled for this clause
                      </div>
                      <div className="flex gap-2 flex-wrap mt-2">
                        {c.preferred_vv_key && (
                          <button onClick={() => installVV(c.preferred_vv_key, c.id)}
                            disabled={installing === c.id}
                            data-testid={`cc-fix-vv-${c.id}`}
                            className="btn-pill btn-pill-primary text-xs disabled:opacity-50">
                            <Library className="w-3 h-3" /> {installing === c.id ? "Installing…" : "Install V&V template"}
                          </button>
                        )}
                        {c.preferred_checklist_key && (
                          <button onClick={() => installChecklist(c.preferred_checklist_key, c.id)}
                            disabled={installing === c.id}
                            data-testid={`cc-fix-ck-${c.id}`}
                            className="btn-pill bg-[#1a1a1a] text-white text-xs hover:bg-[#333] disabled:opacity-50">
                            <Library className="w-3 h-3" /> Install checklist
                          </button>
                        )}
                        {!c.preferred_vv_key && !c.preferred_checklist_key && (
                          <span className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                            Custom clause — create your own V&amp;V item to cover this
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {c.vv_hits && c.vv_hits.length > 0 && (
                        <div className="pt-2">
                          <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a5fb4] font-bold mb-1">
                            V&amp;V items ({c.vv_hit_count})
                          </div>
                          {c.vv_hits.map((v) => (
                            <Link key={v.id} to={`/app/vv/${v.id}`}
                              data-testid={`cc-clause-vv-${v.id}`}
                              className="flex items-center gap-2 text-[12px] py-1 hover:text-[#ff4500]">
                              <ExternalLink className="w-3 h-3" />
                              <span className="font-mono text-[10px] text-[#ff4500] font-bold">{v.display_id}</span>
                              <span className="flex-1">{v.title}</span>
                              <span className="text-[9px] uppercase text-[#1a1a1a]/40">{v.match}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                      {c.checklist_hits && c.checklist_hits.length > 0 && (
                        <div className="pt-1">
                          <div className="text-[10px] font-mono uppercase tracking-wider text-[#a13ad6] font-bold mb-1">
                            Audit / Checklist templates ({c.checklist_hit_count})
                          </div>
                          {c.checklist_hits.map((v) => (
                            <div key={v.id} data-testid={`cc-clause-ck-${v.id}`}
                              className="flex items-center gap-2 text-[12px] py-1">
                              <span className="w-3 h-3 rounded-full bg-[#a13ad6]/30 flex-shrink-0" />
                              <span className="flex-1">{v.name}</span>
                              <span className="text-[9px] uppercase text-[#1a1a1a]/40">{v.match}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <ClauseIndexCardDialog clauseId={viewClauseId} onClose={() => setViewClauseId(null)} />
    </div>
  );
}

function PercentDial({ percent, label }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  const dash = (percent / 100) * c;
  return (
    <div className="relative w-28 h-28" data-testid="cc-dial">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="8" />
        <circle cx="50" cy="50" r={r} fill="none" stroke="#ff4500" strokeWidth="8"
          strokeDasharray={`${dash} ${c}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
        <div className="font-display font-black text-3xl leading-none">{percent}%</div>
        <div className="text-[9px] font-mono uppercase tracking-wider text-white/60 mt-1">{label}</div>
      </div>
    </div>
  );
}
